import moment from 'moment';
import React, { useMemo } from 'react';
import { ELSButton } from '@els/els-component-button-react';
import { ELSCheckBox, ELSIcon } from '@els/els-component-form-field-react';
import { DATE_FORMAT_MAR } from 'constants/app.constant';
import { SNGDate } from 'components/common';
import DrugNameBtnRedirect from './DrugNameBtnRedirect';
import { MarAdministerFormField } from './MarAdministerSidebar';
import MarAdministerTextbox from './MarAdministerTextbox';

interface MarAdministerStep2ItemProps {
  formFieldItem: MarAdministerFormField;
  isSingle: boolean;
  onCheckboxMedication?: Function;
  onAdministrationTimeChange?: Function;
  onAdministrationTimeBlur?: Function;
  onInitialsChange?: Function;
  onInitialsBlur?: Function;
  onCommentChange?: Function;
  addMedication?: Function;
  editMedication?: Function;
  resetMedication?: Function;
  isAuthor?: boolean;
}

const MarAdministerStep2Item = ({
  formFieldItem,
  onCheckboxMedication,
  onAdministrationTimeChange,
  onAdministrationTimeBlur,
  onInitialsChange,
  onInitialsBlur,
  onCommentChange,
  addMedication,
  editMedication,
  resetMedication,
  isSingle,
  isAuthor
}: // eslint-disable-next-line sonarjs/cognitive-complexity
MarAdministerStep2ItemProps) => {
  const { drugName, route, dose, frequency, dosageTimeTable, administrationTime, comment, addedMedication, edit, checkedMedication, initials, medicationChanged } = formFieldItem;
  const administrationTimeValue = useMemo(() => `${moment(dosageTimeTable).format(DATE_FORMAT_MAR)} | ${administrationTime?.value}`, [dosageTimeTable, administrationTime]);
  const disableAddMedication = useMemo(() => {
    return addedMedication && edit;
  }, [addedMedication, edit]);
  const checkedStyle = useMemo(() => {
    return checkedMedication && (!addedMedication || !edit) ? 'checked' : '';
  }, [checkedMedication, addedMedication, edit]);
  const isAfterToday = moment(formFieldItem.dosageTimeTable).isAfter(moment(), 'day');
  const dosageDateTime = `${moment(formFieldItem?.dosageTimeTable).format(DATE_FORMAT_MAR)} | ${formFieldItem?.dosageTimeSeparate} | ${moment(
    formFieldItem?.dosageTimeTable
  ).format('dddd')}`;

  const administrationTimeMapped = { ...administrationTime, label: isAuthor ? 'Administration Time Offset' : 'Administration Time', type: isAuthor ? 'text' : 'time' };

  return (
    <div className={`left-content__order-item ${checkedStyle}`}>
      <div className="item-information">
        <div className="item-information__checkbox">
          {!isSingle && <ELSCheckBox checked={checkedMedication} changeHandler={() => onCheckboxMedication(formFieldItem)} isDisabled={disableAddMedication} />}
          <DrugNameBtnRedirect drugName={drugName} showLink isDisabled={!(checkedMedication && (!addedMedication || !edit))} />
        </div>
        <div className="item-information__details">
          <div className="item-information__details__drug">{`${route} · ${dose} · ${frequency}`}</div>
          <div className="item-information__details__text">{formFieldItem?.specialInstructions}</div>
          <div className="item-information__details__dosage-time">{`Dosage Date & Time: ${dosageDateTime}`}</div>
          {addedMedication && (
            <>
              <div className="item-information__details__dosage-time">{`Administration Date & Time: ${administrationTimeValue}`}</div>
              <div className="item-information__details__comments">{`Comments: ${comment?.value}`}</div>
            </>
          )}
        </div>
        {checkedMedication && (
          <>
            {(!addedMedication || !edit) && (
              <>
                <div className="item-information__input">
                  {isAfterToday && (
                    <div className="item-information__not-same-day">
                      <div className="mar-administer-sidebar__container__info-message">
                        <ELSIcon name="confirmation-outline-circle" size="1x" color="link-inline" customClass="info-message-icon" />
                        <span className="info-message-text">You are administering this medication earlier than planned.</span>
                      </div>
                      <p className="date-label">Administration Date:</p>
                      <SNGDate id={formFieldItem.dosageTimeTable} value={moment(formFieldItem.dosageTimeTable).format(DATE_FORMAT_MAR)} isDisabled onDateChange={() => null} />
                    </div>
                  )}
                  <MarAdministerTextbox
                    formFieldItem={administrationTimeMapped}
                    onChange={(e) => onAdministrationTimeChange(e, formFieldItem)}
                    onBlur={(e) => onAdministrationTimeBlur(e, formFieldItem)}
                    labelVertical
                  />
                  <MarAdministerTextbox
                    formFieldItem={initials}
                    onChange={(e) => onInitialsChange(e, formFieldItem)}
                    onBlur={(e) => onInitialsBlur(e, formFieldItem)}
                    labelVertical
                    placeholder="Enter Initials"
                  />
                  <MarAdministerTextbox
                    formFieldItem={comment}
                    onChange={(e) => onCommentChange(e, formFieldItem)}
                    onBlur={(e) => onCommentChange(e, formFieldItem)}
                    labelVertical
                  />
                </div>
                <div className="item-information__action">
                  <ELSButton
                    text={medicationChanged ? 'Update Medication' : 'Add Medication'}
                    isDisabled={!formFieldItem?.initials?.value}
                    onClick={() => addMedication(formFieldItem)}
                  />
                  <ELSButton
                    isDisabled={!formFieldItem?.initials?.value && !formFieldItem?.comment?.value}
                    text="Reset"
                    type="secondary"
                    onClick={() => resetMedication(formFieldItem)}
                  />
                </div>
              </>
            )}
            {addedMedication && edit && (
              <div className="item-information__action-edit">
                <ELSButton
                  className="item-information__action-edit__edit"
                  iconSize="1x"
                  iconName="edit"
                  iconPosition="left"
                  text="Edit"
                  type="debuttonize"
                  onClick={() => editMedication(formFieldItem)}
                />
                <div>
                  <ELSIcon name="confirmation-solid-circle" size="1x" color="confirm" customClass="passed" />
                  <p>Done</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MarAdministerStep2Item;
